import { Box, MenuItem, Select, Typography, useTheme } from '@mui/material'
import _ from 'lodash'
import { useState } from 'react'
import { IChangelog, IWorkFlow } from '../../definitions/Workflow'
import Formatter from '../../helpers/Formatter'
import Tile from '../layout/Tile'
import Helper from '../../helpers/Helper'

interface P {
    workflow: IWorkFlow
}
export default function WorkflowHistory({ workflow }: P) {
    const theme = useTheme()
    const [dateFilter, setDateFilter] = useState<'this-day' | 'last-3-days' | 'this-week'>('this-week')

    const renderDateFilter = () => {
        return (
            <Box flex={0}>
                <Select
                    size='small'
                    sx={{ height: 30, fontSize: 13, marginRight: 2 }}
                    variant='standard'
                    value={dateFilter}
                    onChange={e => setDateFilter(e.target.value as typeof dateFilter)}>
                    <MenuItem value='this-day'>This day</MenuItem>
                    <MenuItem value='last-3-days'>Last 3 days</MenuItem>
                    <MenuItem value='this-week'>This week</MenuItem>
                </Select>
            </Box>
        )
    }

    // (1000 * 60 * 60 * 24) calculation is used to convert milliseconds to days
    const filterData = (inputData: IChangelog[]): IChangelog[] => {
        if (!Helper.hasVal(inputData) || inputData.length === 0) return
        const today = new Date()

        if (dateFilter === 'this-week')
            return inputData.filter(x => (today.valueOf() - new Date(x.createdAt).valueOf()) / (1000 * 60 * 60 * 24) < 7)
        if (dateFilter === 'last-3-days')
            return inputData.filter(x => (today.valueOf() - new Date(x.createdAt).valueOf()) / (1000 * 60 * 60 * 24) < 3)
        if (dateFilter === 'this-day')
            return inputData.filter(x => (today.valueOf() - new Date(x.createdAt).valueOf()) / (1000 * 60 * 60 * 24) < 1)
    }

    const taskLogs = workflow.changelogs ?? []
    const filteredData = _.orderBy(filterData(taskLogs), x => new Date(x.createdAt), 'desc')

    const groupedLogs = _.groupBy(filteredData, log => {
        return new Date(log?.createdAt).toISOString().split('T')[0]
    })

    const printChangelog = (changelog: IChangelog) => {
        const name = changelog.user?.name
        switch (changelog.activity) {
            case 'task': {
                const afterTask = changelog?.after?.task
                switch (changelog.activityType) {
                    default:
                    case 'complete':
                        return `${name} completed task: ${afterTask?.title}`
                    case 'assigned':
                        return `${name} assigned task to user`
                    case 'new':
                        return `${name} created a new task: ${afterTask}`
                }
            }
            case 'comment':
                return `${name} commented on a task`
            case 'reply':
                return `${name} replied to a comment`
            case 'workflow':
                return `${name} updated the workflow: ${changelog.after.title}`

            default:
                break
        }
    }

    const renderedList = Object.keys(groupedLogs).map(key => {
        const group = groupedLogs[key]
        const date = new Date(key)

        return (
            <Box key={key} padding={1} sx={{ overflowY: 'auto', flex: 0 }}>
                <Typography variant='subtitle2' marginBottom={1}>{Formatter.date(date, 'long')}</Typography>
                {group.map(changelog => {
                    return (
                        <Tile.Row key={'changelog' + changelog.uuid} flex={1} spacing={2} style={{ marginTop: theme.spacing(3) }}>
                            <img src={changelog.user?.image} style={{ height: 40, width: 40, borderRadius: '100%' }} />
                            <Tile.Column spacing={1}>
                                <Typography variant='body2' fontSize={13}>
                                    {printChangelog(changelog)}
                                </Typography>
                                <Typography variant='subtitle2' fontSize={11}>{Formatter.timeAgo(new Date(changelog.createdAt))}</Typography>
                            </Tile.Column>
                        </Tile.Row>
                    )
                })}
            </Box>
        )
    })

    return (
        <>
            <div>{renderDateFilter()}</div>
            <div style={{ flex: 1, height: '100%', overflowY: 'auto', padding: theme.spacing(2) }}>
                {renderedList}
            </div>
        </>
    )
}
